import { useState, useEffect, useRef, useMemo } from "react";
import { useApp } from "../context/AppContext";
import { uid } from "../utils/helpers";
import Badge from "../components/Badge";

export default function ContentView() {
  const { proj, activeId, getStore, setStore, apiFetch, saveContent, flash, dark, t, card, inp, btnP, btnG, btnSm, stitle, muted, th, td } = useApp();

    if (!proj) return null;
    const store = getStore(activeId);
    const catalogClusters = store.clusters || [];
    const [pageType, _setPageType] = useState(store._pageType || "catalog");
    const setPageType = v => { _setPageType(v); setStore(activeId, { ...getStore(activeId), _pageType: v }); };
    const [catItems, setCatItems] = useState(catalogClusters.map(c => ({ ...c, content: c.content || null, cSt: c.cSt || null, cErr: "" })));
    const [prodItems, setProdItems] = useState([]);
    const [filtItems, setFiltItems] = useState([]);
    const [run, setRun] = useState(false);
    const [prog, setProg] = useState({ d: 0, t: 0, ok: 0, er: 0 });
    const [logs, setLogs] = useState([]);
    const [showLog, setShowLog] = useState(true);
    const [bs, setBs] = useState(1);
    const [lim, setLim] = useState(0);
    const [linkRange, setLinkRange] = useState(() => { try { return localStorage.getItem("content_links_" + activeId) || "6-10"; } catch { return "6-10"; } }); // діапазон внутрішніх посилань
    const [flt, setFlt] = useState("all");
    const [pg, setPg] = useState(0);
    const [pvId, setPvId] = useState(null);
    const [impTxt, setImpTxt] = useState("");
    const [showImp, setShowImp] = useState(false);
    const abRef = useRef(false);
    const lgRef = useRef();
    const fRef = useRef();
    const PP = 20;

    // ── Formula state (persisted in dataRef to survive remounts) ──
    // template: string (text with {var} placeholders), variants: string[] (text variations),
    // defs: { varName: { label } }, vars: { varName: [val1, val2, ...] }
    const fmlRef = useRef(store._formula || { template: "", variants: [], defs: {}, vars: {}, loaded: false });
    const [fmlTemplate, _setFmlTemplate] = useState(fmlRef.current.template);
    const [fmlVariants, _setFmlVariants] = useState(fmlRef.current.variants || []);
    const [fmlDefs, _setFmlDefs] = useState(fmlRef.current.defs);
    const [fmlVars, _setFmlVars] = useState(fmlRef.current.vars);
    const [fmlSaving, setFmlSaving] = useState(false);
    const [fmlLoaded, setFmlLoaded] = useState(fmlRef.current.loaded);
    const [fmlGenTag, setFmlGenTag] = useState(null);
    const [fmlBusy, setFmlBusy] = useState(false);
    const [fmlStep, setFmlStep] = useState({ text: "", pct: 0 });
    const [fmlExample, setFmlExample] = useState(null);
    const [fmlShowRaw, setFmlShowRaw] = useState(false);
    const fmlSync = () => setStore(activeId, { ...getStore(activeId), _formula: fmlRef.current });
    const setFmlTemplate = v => { const val = typeof v === "function" ? v(fmlRef.current.template) : v; fmlRef.current.template = val; _setFmlTemplate(val); fmlSync(); };
    const setFmlVariants = v => { const val = typeof v === "function" ? v(fmlRef.current.variants || []) : v; fmlRef.current.variants = val; _setFmlVariants(val); fmlSync(); };
    const setFmlDefs = v => { const val = typeof v === "function" ? v(fmlRef.current.defs) : v; fmlRef.current.defs = val; _setFmlDefs(val); fmlSync(); };
    const setFmlVars = v => { const val = typeof v === "function" ? v(fmlRef.current.vars) : v; fmlRef.current.vars = val; _setFmlVars(val); fmlSync(); };

    useEffect(() => { setStore(activeId, { ...getStore(activeId), clusters: catItems }); }, [catItems]);
    useEffect(() => { lgRef.current && (lgRef.current.scrollTop = lgRef.current.scrollHeight); }, [logs]);

    // Load saved formula from API (once)
    useEffect(() => {
      if (fmlLoaded) return;
      (async () => {
        try {
          const res = await apiFetch(`/api/projects/${activeId}/formula`);
          const data = await res.json();
          if (data) {
            setFmlTemplate(data.template || "");
            setFmlVariants(data.tag_definitions?.___variants || []);
            const defs = { ...(data.tag_definitions || {}) }; delete defs.___variants;
            setFmlDefs(defs);
            setFmlVars(data.tag_variants || {});
          }
        } catch (e) { console.error("Load formula:", e); }
        fmlRef.current.loaded = true;
        setFmlLoaded(true);
        setStore(activeId, { ...getStore(activeId), _formula: fmlRef.current });
      })();
    }, [activeId, fmlLoaded]);

    const log = (m, t = "ok") => setLogs(l => [...l.slice(-300), { m, t, ts: new Date().toLocaleTimeString() }]);
    useEffect(() => { if (!activeId) return; try { localStorage.setItem("content_links_" + activeId, linkRange); } catch { /* ignore */ } }, [linkRange, activeId]);
    const cur = pageType === "catalog" ? catItems : pageType === "product" ? prodItems : filtItems;
    const setCur = pageType === "catalog" ? setCatItems : pageType === "product" ? setProdItems : setFiltItems;

    const allLinks = useMemo(() => catalogClusters.filter(c => (c.keywords || []).length >= 2).map(c => ({ url: c.url, anchor: c.hfKeyword })), [catalogClusters]);

    // Normalize URL for comparison (strip trailing slash, query, hash, lowercase)
    const normUrl = (u) => {
      if (!u) return "";
      try {
        const url = new URL(u, "http://x");
        return (url.pathname.replace(/\/$/, "") + url.search).toLowerCase();
      } catch { return String(u).replace(/\/$/, "").toLowerCase().split("?")[0].split("#")[0]; }
    };

    // Remove self-referencing <a> tags from generated HTML (keeps anchor text)
    const stripSelfLinks = (html, selfUrl) => {
      if (!html) return html;
      const self = normUrl(selfUrl);
      return html.replace(/<a\s+[^>]*href=["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi, (m, href, txt) => {
        return normUrl(href) === self ? txt : m;
      });
    };

    // Import CSV
    const handleCSV = (file) => {
      const r = new FileReader();
      r.onload = ev => {
        const lines = ev.target.result.trim().split(/\r?\n/);
        if (lines.length < 2) return;
        const sep = lines[0].includes("\t") ? "\t" : lines[0].includes(";") ? ";" : ",";
        const hdr = lines[0].split(sep).map(h => h.trim().toLowerCase().replace(/['"]/g, ""));
        const ui = hdr.findIndex(h => ["url","page","адрес","ссылка"].includes(h));
        const ni = hdr.findIndex(h => ["name","title","название","товар","назва"].includes(h));
        if (ui < 0) { flash("Нет колонки url"); return; }
        const items = [];
        for (let i = 1; i < lines.length; i++) {
          const c = lines[i].split(sep).map(s => s.trim().replace(/^['"]|['"]$/g, ""));
          const url = c[ui]; if (!url) continue;
          const name = ni >= 0 ? c[ni] : url.split("/").pop()?.replace(/[-_?=]/g, " ") || "";
          items.push({ id: uid(), url, hfKeyword: name, keywords: [{ keyword: name, frequency: 100 }], allCount: 1, status: "READY", content: null, cSt: null, cErr: "" });
        }
        if (pageType === "product") setProdItems(items); else setFiltItems(items);
        setShowImp(false);
        flash(`${items.length} страниц загружено`);
      };
      r.readAsText(file);
    };

    const handleTxtImport = () => {
      const items = impTxt.trim().split(/\n/).filter(Boolean).map(line => {
        const p = line.split(/[,;\t]/).map(s => s.trim());
        return { id: uid(), url: p[0], hfKeyword: p[1] || p[0].split("/").pop()?.replace(/[-_?=]/g, " ") || "", keywords: [{ keyword: p[1] || "", frequency: 100 }], allCount: 1, status: "READY", content: null, cSt: null, cErr: "" };
      });
      if (pageType === "product") setProdItems(items); else setFiltItems(items);
      setShowImp(false); setImpTxt(""); flash(`${items.length} загружено`);
    };

    async function genContent(cl) {
      const kwList = cl.keywords?.slice(0, 12).map(k => `${k.keyword} (${k.frequency})`).join(", ") || cl.hfKeyword;
      const cands = allLinks.filter(u => u.url !== cl.url).sort(() => Math.random() - 0.5);
      let sys, usr, mt;
      const _lr = String(linkRange).split("-").map(Number); const lmin = _lr[0] || 3, lmax = _lr[1] || lmin, lmid = Math.max(1, Math.round((lmin + lmax) / 2));

      if (pageType === "catalog") {
        const lk = cands.slice(0, 45).map(l => `"${l.anchor}" → ${l.url}`).join("\n");
        mt = 4096;
        sys = `Ты SEO-копирайтер интернет-магазина. Напиши текст для каталога.

СТРУКТУРА: <h2> с "${cl.hfKeyword}" + 2-3 коротких абзаца

ОБЯЗАТЕЛЬНО:
- СТРОГО 1000-1300 символов чистого текста (без HTML-тегов). Превышение 1300 = провал!
- ПРОПОРЦИЯ: 70% полезный текст, 30% анкорные ссылки
- Текст должен быть информативным, полезным для покупателя
- Описывай категорию, преимущества, как выбрать, для кого подходит

АНКОРЫ:
- Вставь ${lmin}-${lmax} ссылок <a href="URL">анкор</a> РАВНОМЕРНО по тексту
- НЕ спамь анкорами — между ссылками должен быть смысловой текст
- Бери ТОЛЬКО из списка ниже

ПРАВИЛА: текст про ${cl.url}, ключи: ${kwList}, язык=язык ключей, только H2, не пиши "звоните"

ССЫЛКИ (вставь ${lmin}-${lmax} равномерно):
${lk}

JSON: {"h2":"...","html":"<p>...</p>","anchor_count":${lmid},"used_keywords":["k1"]}

ВЕРНИ ТІЛЬКИ JSON-обʼєкт без преамбули, без коментарів, без markdown-обгорток. Перший символ відповіді = "{", останній = "}".`;
        usr = `URL: ${cl.url}\nКлюч: ${cl.hfKeyword}${proj?.version ? `\nВерсія сайту: ${proj.version}. Генеруй контент саме для цієї версії.` : ""}\n2-3 абзаца, СТРОГО 1000-1300 символов текста, 70% текст / 30% анкоры`;
      } else if (pageType === "product") {
        const lk = cands.slice(0, 15).map(l => `"${l.anchor}" → ${l.url}`).join("\n");
        mt = 3000;
        sys = `Напиши описание товара "${cl.hfKeyword}" для карточки.
<h2> + 2-3 абзаца. МИНИМУМ 1000 символов чистого текста.
ПРОПОРЦИЯ: 70% полезный текст (описание, характеристики, преимущества), 30% анкоры.
Вставь ${lmin}-${lmax} ссылок на каталоги <a href="URL">анкор</a> равномерно.

ССЫЛКИ: ${lk}

JSON: {"h2":"...","html":"<p>...</p>","anchor_count":${lmid},"used_keywords":["k1"]}

ВЕРНИ ТІЛЬКИ JSON-обʼєкт без преамбули, без коментарів, без markdown-обгорток. Перший символ відповіді = "{", останній = "}".`;
        usr = `Товар: ${cl.hfKeyword}\nURL: ${cl.url}${proj?.version ? `\nВерсія сайту: ${proj.version}. Генеруй контент саме для цієї версії.` : ""}\nСТРОГО 1000-1300 символів, 70% текст / 30% анкоры`;
      } else {
        const lk = cands.slice(0, 40).map(l => `"${l.anchor}" → ${l.url}`).join("\n");
        mt = 4096;
        sys = `Напиши текст для фильтровой страницы "${cl.hfKeyword}".
<h2> + 3-5 абзацев. МИНИМУМ 1000 символов чистого текста.
ПРОПОРЦИЯ: 70% полезный текст (описание подборки, как выбрать, для кого), 30% анкорные ссылки.
Вставь ${lmin}-${lmax} ссылок <a href="URL">анкор</a> равномерно по тексту.

ССЫЛКИ: ${lk}

JSON: {"h2":"...","html":"<p>...</p>","anchor_count":${lmid},"used_keywords":["k1"]}

ВЕРНИ ТІЛЬКИ JSON-обʼєкт без преамбули, без коментарів, без markdown-обгорток. Перший символ відповіді = "{", останній = "}".`;
        usr = `Фильтр: ${cl.hfKeyword}\nURL: ${cl.url}${proj?.version ? `\nВерсія сайту: ${proj.version}. Генеруй контент саме для цієї версії.` : ""}\nСТРОГО 1000-1300 символів, 70% текст / 30% анкоры`;
      }

      const _tk = localStorage.getItem("metago_token");
      const resp = await fetch("/api/claude", {
        method: "POST", headers: { "Content-Type": "application/json", ...(_tk ? { Authorization: `Bearer ${_tk}` } : {}) },
        body: JSON.stringify({ model: "claude-sonnet-4-6", max_tokens: mt, system: sys, messages: [{ role: "user", content: usr }] })
      });
      if (!resp.ok) throw new Error(`API ${resp.status}: ${(await resp.text().catch(() => "")).slice(0, 120)}`);
      const d = await resp.json();
      const txt = d.content?.find(b => b.type === "text")?.text || "";
      let parsed;
      const tryParse = (raw) => {
        try { return JSON.parse(raw); } catch { return null; }
      };
      // Strategy:
      // 1. strip ```json fences
      // 2. try parse whole
      // 3. extract first balanced { ... } from text (greedy)
      let cleaned = txt.replace(/```json\s*/gi, "").replace(/```/g, "").trim();
      parsed = tryParse(cleaned);
      if (!parsed) {
        // Find first { and last } and try
        const first = cleaned.indexOf("{");
        const last = cleaned.lastIndexOf("}");
        if (first >= 0 && last > first) {
          parsed = tryParse(cleaned.slice(first, last + 1));
        }
      }
      if (!parsed) {
        // Try same with arrays
        const first = cleaned.indexOf("[");
        const last = cleaned.lastIndexOf("]");
        if (first >= 0 && last > first) {
          parsed = tryParse(cleaned.slice(first, last + 1));
        }
      }
      if (!parsed) {
        throw new Error("JSON parse error: " + txt.slice(0, 80));
      }
      if (parsed?.html) parsed.html = stripSelfLinks(parsed.html, cl.url);
      return parsed;
    }

    const generate = async () => {
      if (run) return; abRef.current = false; setRun(true); setLogs([]);
      let ready = cur.filter(c => c.url && (c.keywords?.length || 0) > 0 && !c.content);
      const todo = lim > 0 ? ready.slice(0, lim) : ready;
      if (!todo.length) { flash("Нет URL"); setRun(false); return; }
      setProg({ d: 0, t: todo.length, ok: 0, er: 0 });
      log(`Старт [${pageType}]: ${todo.length}, batch=${bs}`);
      let okN = 0, erN = 0;
      const results = new Map(); // id -> { cl, content, status, error }
      for (let i = 0; i < todo.length; i += bs) {
        if (abRef.current) { log("⛔ Стоп", "warn"); break; }
        const batch = todo.slice(i, i + bs);
        log(`📦 ${Math.floor(i/bs)+1}/${Math.ceil(todo.length/bs)}`);
        setCur(p => p.map(c => batch.find(b => b.id === c.id) ? { ...c, cSt: "GENERATING" } : c));
        await Promise.allSettled(batch.map(async cl => {
          let attempt = 0;
          const maxAttempts = 8;
          while (attempt < maxAttempts) {
            try {
              const content = await genContent(cl);
              const ac = (content.html?.match(/<a /g) || []).length;
              results.set(cl.id, { cl, content, status: "DONE", error: "" });
              setCur(p => p.map(c => c.id === cl.id ? { ...c, content, cSt: "DONE", cErr: "" } : c));
              okN++; log(`✓ ${cl.hfKeyword.slice(0, 25)} → ${ac} анкоров`); return;
            } catch (err) {
              attempt++;
              const msg = err.message || "";
              const isOverload = /\b(529|overload|rate[_ ]?limit|429)\b/i.test(msg);
              if (attempt < maxAttempts) {
                // Exponential backoff: 3s, 6s, 12s, 24s... (cap at 60s) for overload; 2.5s flat for other errors
                const wait = isOverload ? Math.min(3000 * Math.pow(2, attempt - 1), 60000) : 2500;
                log(`🔄 ${isOverload ? "overload" : msg.slice(0, 30)} → wait ${Math.round(wait/1000)}s (${attempt}/${maxAttempts})`, "warn");
                await new Promise(r => setTimeout(r, wait));
              } else {
                results.set(cl.id, { cl, content: null, status: "ERROR", error: msg });
                setCur(p => p.map(c => c.id === cl.id ? { ...c, cSt: "ERROR", cErr: msg } : c));
                erN++; log(`✕ ${msg.slice(0, 50)}`, "err");
              }
            }
          }
        }));
        setProg({ d: Math.min(i + bs, todo.length), t: todo.length, ok: okN, er: erN });
        // Save after each batch to prevent data loss (use local results, not stale `cur`)
        const batchSave = Array.from(results.values())
          .filter(r => r.content)
          .map(r => ({ id: r.cl.id, clusterId: r.cl.id, pageType, url: r.cl.url, keywords: r.cl.keywords, content: r.content, status: r.status, error: r.error }));
        if (batchSave.length) saveContent(activeId, batchSave);
        if (i + bs < todo.length && !abRef.current) await new Promise(r => setTimeout(r, 2000));
      }
      setRun(false); log(`🏁 ✓${okN} ✕${erN}`); flash(`${okN}✓ ${erN}✕`);
      // Final save of all accumulated results
      const saveItems = Array.from(results.values())
        .filter(r => r.content)
        .map(r => ({ id: r.cl.id, clusterId: r.cl.id, pageType, url: r.cl.url, keywords: r.cl.keywords, content: r.content, status: r.status, error: r.error }));
      if (saveItems.length) saveContent(activeId, saveItems);
    };

    const regenOne = async cl => {
      if (run) return;
      setCur(p => p.map(c => c.id === cl.id ? { ...c, cSt: "GENERATING", content: null } : c));
      try {
        const ct = await genContent(cl);
        setCur(p => p.map(c => c.id === cl.id ? { ...c, content: ct, cSt: "DONE", cErr: "" } : c));
        saveContent(activeId, [{ id: cl.id, clusterId: cl.id, pageType, url: cl.url, keywords: cl.keywords, content: ct, status: "DONE", error: "" }]);
      }
      catch (err) { setCur(p => p.map(c => c.id === cl.id ? { ...c, cSt: "ERROR", cErr: err.message } : c)); }
    };

    const expHTML = () => {
      const done = cur.filter(c => c.content);
      const h = done.map(c => `<!-- ${c.url} -->\n<h2>${c.content.h2}</h2>\n${c.content.html}`).join("\n<hr>\n");
      const b = new Blob([`<html><head><meta charset="utf-8"><style>body{font-family:sans-serif;max-width:900px;margin:40px auto;line-height:1.7}h2{color:#333;border-bottom:2px solid #6366f1;padding-bottom:8px}a{color:#6366f1}hr{margin:40px 0;border:none;border-top:2px dashed #eee}p{margin:12px 0}</style></head><body>${h}</body></html>`], { type: "text/html;charset=utf-8" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = `${proj.name}_${pageType}.html`; a.click();
    };
    const expCSV = () => {
      const esc = s => `"${(s||"").replace(/"/g,'""')}"`;
      const h = "\uFEFFurl,h2,html,anchors,chars\n";
      const r = cur.filter(c => c.content).map(c => [esc(c.url), esc(c.content.h2), esc(c.content.html), (c.content.html?.match(/<a /g)||[]).length, c.content.html?.replace(/<[^>]+>/g,"").length||0].join(",")).join("\n");
      const b = new Blob([h+r], { type: "text/csv;charset=utf-8" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = `${proj.name}_${pageType}.csv`; a.click();
    };
    const expSheet = async () => {
      const done = cur.filter(c => c.content);
      if (!done.length) { flash("Немає згенерованого контенту"); return; }
      try {
        flash("Створення Google Sheet...");
        const r = await apiFetch(`/api/projects/${activeId}/content/export-sheet`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            items: done.map(c => ({ url: c.url, keywords: c.keywords, content: c.content })),
            projectName: proj.name,
            pageType,
          }),
        });
        const d = await r.json();
        if (d.error) { flash("Помилка: " + d.error); return; }
        if (d.url) { window.open(d.url, "_blank"); flash("Google Sheet створено"); }
      } catch (e) { flash("Помилка: " + e.message); }
    };

    const cn = { all: cur.length, done: 0, ready: 0, error: 0 };
    cur.forEach(c => { if (c.content) cn.done++; else if (c.url && (c.keywords?.length || 0) > 0) cn.ready++; if (c.cSt === "ERROR") cn.error++; });

    const fData = flt === "all" ? cur :
      flt === "done" ? cur.filter(c => c.content) : flt === "ready" ? cur.filter(c => c.url && (c.keywords?.length || 0) > 0 && !c.content) :
      cur.filter(c => c.cSt === "ERROR");
    const tPg = Math.ceil(fData.length / PP);
    const pgD = fData.slice(pg * PP, (pg + 1) * PP);
    const pct = prog.t ? Math.round(prog.d / prog.t * 100) : 0;
    const pvItem = pvId ? cur.find(c => c.id === pvId) : null;

    const hlHTML = (html) => html?.replace(/<a\s+href="([^"]*)"[^>]*>(.*?)<\/a>/gi,
      '<a href="$1" style="background:#dbeafe;color:#1d4ed8;padding:1px 4px;border-radius:3px;text-decoration:none;border-bottom:2px solid #60a5fa;font-weight:600">$2</a>') || "";

    return (
      <div>
        <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>📝 Генерация контента</h2>
        <p style={{ ...muted, marginBottom: 14 }}>{proj.name}</p>

        {/* Type tabs */}
        <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
          {[["catalog", "📁 Каталог", catItems.length, "внутр. посилання"],
            ["product", "🛍 Товари", prodItems.length, "~500с + посилання"],
            ["filter", "🔍 Фільтри", filtItems.length, "внутр. посилання"],
            ["formula", "🧪 Формула", 0, "Конструктор"]
          ].map(([k, l, n, h]) => (
            <div key={k} onClick={() => { setPageType(k); setPg(0); setFlt("all"); }}
              style={{ flex: 1, padding: "12px 14px", borderRadius: 12, cursor: "pointer", textAlign: "center",
                background: pageType === k ? "linear-gradient(135deg,#6366f1,#8b5cf6)" : "#fff",
                color: pageType === k ? "#fff" : "#333",
                border: pageType === k ? "none" : "1px solid #e5e5e5",
                boxShadow: pageType === k ? "0 4px 12px rgba(99,102,241,.3)" : "0 1px 3px rgba(0,0,0,.04)" }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{l}</div>
              <div style={{ fontSize: 11, opacity: .8, marginTop: 2 }}>{n > 0 ? `${n} стр.` : h}</div>
            </div>
          ))}
        </div>

        {/* ═══ FORMULA TAB ═══ */}
        {pageType === "formula" && (() => {
          const fmlParsedVars = [...new Set((fmlTemplate.match(/\{[^}]+\}/g) || []).map(m => m.replace(/[{}]/g, "")))];

          // ── Auto-generate: n-grams → variables, AI → text template + variants ──
          const fmlAutoGenerate = async () => {
            if (fmlBusy) return;
            setFmlBusy(true);
            try {
              // Step 1: Collect keywords from clusters (priority) + n-grams
              setFmlStep({ text: "Сбор ключей из семантики...", pct: 5 });
              await new Promise(r => setTimeout(r, 30));

              // 1a: Keywords from clusters — group by shared words
              const allKws = [];
              catalogClusters.forEach(c => {
                (c.keywords || []).forEach(k => {
                  if (k.keyword && k.keyword.length >= 3) {
                    allKws.push({ gram: k.keyword.toLowerCase().trim(), count: k.frequency || 1, words: k.keyword.toLowerCase().trim().split(/\s+/) });
                  }
                });
              });
              // Deduplicate keywords
              const kwMap = {};
              allKws.forEach(k => {
                if (!kwMap[k.gram] || kwMap[k.gram].count < k.count) kwMap[k.gram] = k;
              });
              const uniqueKws = Object.values(kwMap);

              // 1b: Load n-grams as supplement
              setFmlStep({ text: "Загрузка N-грам...", pct: 10 });
              await new Promise(r => setTimeout(r, 30));
              let ngramItems = [];
              try {
                const res = await apiFetch(`/api/projects/${activeId}/ngrams/saved`);
                const saved = await res.json();
                if (saved && saved.data) {
                  const { bigrams = [], trigrams = [] } = saved.data;
                  ngramItems = [
                    ...bigrams.filter(g => g.count >= 3).map(g => ({ gram: g.gram, count: g.count, words: g.gram.split(" ") })),
                    ...trigrams.filter(g => g.count >= 3).map(g => ({ gram: g.gram, count: g.count, words: g.gram.split(" ") })),
                  ];
                }
              } catch (e) { console.log("No ngrams, using keywords only"); }

              // Merge: keywords first, then n-grams (skip duplicates)
              const allGrams = [...uniqueKws];
              const gramSet = new Set(uniqueKws.map(k => k.gram));
              ngramItems.forEach(g => { if (!gramSet.has(g.gram)) { allGrams.push(g); gramSet.add(g.gram); } });

              if (!allGrams.length) { flash("Нет ключей в кластерах. Загрузите семантику."); setFmlBusy(false); return; }

              // Step 2: Group by shared words
              setFmlStep({ text: "Группировка ключей...", pct: 15 });
              await new Promise(r => setTimeout(r, 30));
              const wordToGrams = {};
              allGrams.forEach(g => { g.words.forEach(w => { if (w.length >= 3) { if (!wordToGrams[w]) wordToGrams[w] = []; wordToGrams[w].push(g); } }); });
              const sortedWords = Object.entries(wordToGrams).sort((a, b) => b[1].length - a[1].length).filter(([, gs]) => gs.length >= 2);
              const usedGrams = new Set();
              const groups = [];
              for (const [word, gs] of sortedWords) {
                const unused = gs.filter(g => !usedGrams.has(g.gram));
                if (unused.length < 2) continue;
                groups.push({ label: word, grams: unused });
                unused.forEach(g => usedGrams.add(g.gram));
                if (groups.length >= 10) break;
              }
              if (!groups.length) { flash("Не удалось сгруппировать ключи"); setFmlBusy(false); return; }

              // Step 3: Build variable definitions — H2 + tags (keyword groups) + ungrouped keys + text variants
              setFmlStep({ text: "Создание переменных...", pct: 25 });
              await new Promise(r => setTimeout(r, 30));
              const newDefs = {}, newVars = {};
              const varNames = [];

              // H2 — always first
              varNames.push("H2");
              newDefs["H2"] = { label: "H2 заголовок страницы" };
              newVars["H2"] = catalogClusters.slice(0, 15).map(c => c.hfKeyword).filter(Boolean);

              // Grouped tags
              groups.forEach((gr, i) => {
                const tn = `tags_${i + 1}`;
                varNames.push(tn);
                newDefs[tn] = { label: gr.label };
                newVars[tn] = gr.grams.slice(0, 15).map(g => g.gram);
              });

              // Collect ALL ungrouped keywords into extra tags
              const ungrouped = allGrams.filter(g => !usedGrams.has(g.gram));
              if (ungrouped.length > 0) {
                // Split ungrouped into chunks of ~10 for separate tags
                const chunkSize = 10;
                let tagIdx = groups.length + 1;
                for (let ci = 0; ci < ungrouped.length; ci += chunkSize) {
                  const chunk = ungrouped.slice(ci, ci + chunkSize);
                  const tn = `tags_${tagIdx}`;
                  varNames.push(tn);
                  newDefs[tn] = { label: `ключи ${tagIdx}` };
                  newVars[tn] = chunk.map(g => g.gram);
                  tagIdx++;
                  if (tagIdx > 20) break; // max 20 tag groups
                }
              }

              // Add text variables — ~2x more than tag groups (70% text, 30% anchors)
              const totalTags = varNames.filter(v => v.startsWith("tags_")).length;
              const textCount = Math.max(5, Math.min(10, totalTags * 2));
              for (let ti = 1; ti <= textCount; ti++) {
                const tn = `text_${ti}`;
                varNames.push(tn);
                newDefs[tn] = { label: `Статичный текст ${ti}` };
                newVars[tn] = [];
              }
              setFmlDefs(newDefs);
              setFmlVars(newVars);

              // Step 4: AI generates the full text template with variables
              setFmlStep({ text: "AI создаёт шаблон текста...", pct: 35 });
              const domain = proj?.domain || "";
              const h2ex = catalogClusters.length ? catalogClusters[0].hfKeyword : "";
              const tagsDesc = varNames.filter(v => v.startsWith("tags_") || v === "H2").map(v => `{${v}} — ${newDefs[v].label} (примеры: ${(newVars[v] || []).slice(0, 3).join(", ")})`).join("\n");
              const textsDesc = varNames.filter(v => v.startsWith("text_")).map(v => `{${v}} — динамический текст (5-15 слов)`).join("\n");
              const _tk = localStorage.getItem("metago_token");

              try {
                const resp = await fetch("/api/claude", {
                  method: "POST",
                  headers: { "Content-Type": "application/json", ...(_tk ? { Authorization: `Bearer ${_tk}` } : {}) },
                  body: JSON.stringify({
                    model: "claude-sonnet-4-6", max_tokens: 4096,
                    system: `Ты SEO-копирайтер. Создай шаблон SEO-текста для сайта.

ПРОПОРЦИЯ: 30% анкоры ({tags_N}), 70% динамический текст ({text_N}).
Текст должен быть ПРЕИМУЩЕСТВЕННО из {text_N} переменных с вкраплениями {tags_N}.

2 типа переменных:
- {tags_N} — ключевые SEO-фразы/анкоры (30% текста)
- {text_N} — динамические куски текста (70% текста), 5-15 слов каждый, у каждого 7-10 вариантов

ПРИМЕР правильной структуры:
"{H2} — {text_1}. {text_2} {tags_1} {text_3}. {text_4} {tags_2} {text_5}. {text_6} {tags_1} {text_7}. {text_8} {tags_3} {text_9} {text_10}."

ПРАВИЛА:
- Текст 2 абзаца, СТРОГО 1000-1500 символов
- {H2} — H2 заголовок, используй 1 раз в начале
- На каждый {tags_N} должно приходиться 2-3 штуки {text_N}!
- {text_N} — это ОСНОВА текста, полноценные смысловые куски (5-15 слов)
- {tags_N} — только вставки-анкоры между текстовыми блоками
- Каждый {tags_N} используй 1-2 раза, каждый {text_N} используй 1-2 раза
- РАВНОМЕРНО распредели по обоим абзацам
- НЕ выдумывай новые переменные — ТОЛЬКО предоставленные

Отвечай JSON: {"template": "текст", "text_variants": {"text_1": ["вар1", "вар2", ...], ...}}
Для КАЖДОГО text_N создай 7-10 вариантов осмысленных фраз (5-15 слов) подходящих по контексту.`,
                    messages: [{ role: "user", content: `Домен: ${domain}${proj?.version ? `\nВерсія сайту: ${proj.version}. Генеруй контент саме для цієї версії.` : ""}\nТематика: ${h2ex}\n\nАнкоры (30%):\n${tagsDesc}\n\nТекстовые блоки (70%):\n${textsDesc}\n\nТекст: 70% текстовые блоки, 30% анкоры. Макс 1500 символов.` }]
                  })
                });
                if (resp.ok) {
                  const d = await resp.json();
                  const txt = d.content?.find(b => b.type === "text")?.text || "";
                  const parsed = JSON.parse(txt.replace(/```json\s*/gi, "").replace(/```/g, "").trim());
                  if (parsed.template) setFmlTemplate(parsed.template);
                  // Fill text_N variants from AI response
                  if (parsed.text_variants) {
                    setFmlVars(prev => {
                      const updated = { ...prev };
                      Object.entries(parsed.text_variants).forEach(([k, vals]) => {
                        if (k.startsWith("text_") && Array.isArray(vals)) updated[k] = vals;
                      });
                      return updated;
                    });
                  }
                }
              } catch (e) { console.error("AI template:", e); }

              // Step 5: AI generates text variants (alternative phrasings)
              setFmlStep({ text: "AI создаёт варианты текста...", pct: 60 });
              const newVariants = [];
              for (let vi = 0; vi < 3; vi++) {
                setFmlStep({ text: `AI вариант ${vi + 1}/3...`, pct: 60 + vi * 12 });
                try {
                  const currentTmpl = fmlRef.current.template;
                  if (!currentTmpl || currentTmpl.length < 50) continue;
                  const resp2 = await fetch("/api/claude", {
                    method: "POST",
                    headers: { "Content-Type": "application/json", ...(_tk ? { Authorization: `Bearer ${_tk}` } : {}) },
                    body: JSON.stringify({
                      model: "claude-sonnet-4-6", max_tokens: 3000,
                      system: `Перефразируй SEO-текст.${proj?.version ? ` Версія сайту: ${proj.version}.` : ""} Сохрани ВСЕ переменные в {фигурных скобках} без изменений. Можно менять порядок переменных и текст между ними. Пропорция: 70% текст ({text_N}), 30% анкоры ({tags_N}). СТРОГО до 1500 символов. Отвечай ТОЛЬКО текстом.`,
                      messages: [{ role: "user", content: `Оригинал:\n${currentTmpl}\n\nПерефразируй (вариант ${vi + 1}). Сохрани все {переменные}. 70% текст, 30% анкоры.` }]
                    })
                  });
                  if (resp2.ok) {
                    const d2 = await resp2.json();
                    const vtxt = d2.content?.find(b => b.type === "text")?.text || "";
                    if (vtxt.length > 50) newVariants.push(vtxt.trim());
                  }
                } catch (e) { console.error("AI variant " + vi + ":", e); }
              }
              if (newVariants.length) setFmlVariants(newVariants);

              setFmlStep({ text: "Готово!", pct: 100 });
              flash(`Формула: ${varNames.length} переменных, ${newVariants.length + 1} вариантов текста`);
              setTimeout(() => setFmlStep({ text: "", pct: 0 }), 2000);
            } catch (e) { flash("Ошибка: " + e.message); }
            setFmlBusy(false);
          };

          // ── AI generate more values for a variable ──
          const fmlAiGenerate = async (varName) => {
            setFmlGenTag(varName);
            try {
              const existing = fmlVars[varName] || [];
              const _tk = localStorage.getItem("metago_token");
              const resp = await fetch("/api/claude", {
                method: "POST",
                headers: { "Content-Type": "application/json", ...(_tk ? { Authorization: `Bearer ${_tk}` } : {}) },
                body: JSON.stringify({
                  model: "claude-sonnet-4-6", max_tokens: 1024,
                  system: "Ты SEO-специалист. Сгенерируй 7-10 коротких фраз-вариантов для подстановки в SEO-текст. Отвечай ТОЛЬКО JSON массивом строк.",
                  messages: [{ role: "user", content: `Переменная: {${varName}} (${fmlDefs[varName]?.label || ""})\nДомен: ${proj?.domain || ""}${proj?.version ? `\nВерсія сайту: ${proj.version}. Генеруй контент саме для цієї версії.` : ""}\nСуществующие: ${existing.slice(0, 10).join(", ")}\n\nСгенерируй 7-10 НОВЫХ фраз. JSON массив строк.` }]
                })
              });
              if (!resp.ok) throw new Error(`API ${resp.status}`);
              const d = await resp.json();
              const txt = d.content?.find(b => b.type === "text")?.text || "";
              const parsed = JSON.parse(txt.replace(/```json\s*/gi, "").replace(/```/g, "").trim());
              if (Array.isArray(parsed)) { setFmlVars(prev => ({ ...prev, [varName]: [...(prev[varName] || []), ...parsed] })); flash(`+${parsed.length} вариантов`); }
            } catch (e) { flash("Ошибка AI: " + e.message); }
            setFmlGenTag(null);
          };

          // ── Save ──
          const fmlSave = async () => {
            setFmlSaving(true);
            try {
              const defsToSave = { ...fmlDefs, ___variants: fmlVariants };
              await apiFetch(`/api/projects/${activeId}/formula`, { method: "PUT", body: JSON.stringify({ template: fmlTemplate, tag_definitions: defsToSave, tag_variants: fmlVars }) });
              flash("Формула сохранена");
            } catch (e) { flash("Ошибка сохранения"); }
            setFmlSaving(false);
          };

          // ── Variable helpers ──
          const fmlUpdateVariant = (vn, idx, val) => { setFmlVars(prev => { const a = [...(prev[vn] || [])]; a[idx] = val; return { ...prev, [vn]: a }; }); };
          const fmlAddVariant = (vn) => { setFmlVars(prev => ({ ...prev, [vn]: [...(prev[vn] || []), ""] })); };
          const fmlRemoveVariant = (vn, idx) => { setFmlVars(prev => { const a = [...(prev[vn] || [])]; a.splice(idx, 1); return { ...prev, [vn]: a }; }); };
          const fmlUpdateLabel = (vn, lb) => { setFmlDefs(prev => ({ ...prev, [vn]: { ...prev[vn], label: lb } })); };
          const fmlRemoveVar = (vn) => {
            setFmlDefs(prev => { const n = { ...prev }; delete n[vn]; return n; });
            setFmlVars(prev => { const n = { ...prev }; delete n[vn]; return n; });
          };
          const fmlAddVar = () => {
            const nums = Object.keys(fmlDefs).map(k => { const m = k.match(/\d+/); return m ? parseInt(m[0]) : 0; });
            const next = Math.max(0, ...nums) + 1;
            const vn = `tags_${next}`;
            setFmlDefs(prev => ({ ...prev, [vn]: { label: "" } }));
            setFmlVars(prev => ({ ...prev, [vn]: [""] }));
          };

          // ── Build example: replace {vars} with random values ──
          // Returns { segments: [{text, type, varName}], plain: "string" }
          const buildExample = (tmpl) => {
            const text = tmpl || fmlTemplate;
            if (!text) return { segments: [], plain: "" };
            const segments = [];
            let last = 0;
            const re = /\{([^}]+)\}/g;
            let m;
            while ((m = re.exec(text)) !== null) {
              if (m.index > last) segments.push({ text: text.slice(last, m.index), type: "static" });
              const varName = m[1];
              let val;
              const vals = fmlVars[varName] || [];
              val = vals.length ? vals[Math.floor(Math.random() * vals.length)] : `{${varName}}`;
              const varType = varName.startsWith("text_") ? "text" : "anchor";
              segments.push({ text: val, type: varType, varName });
              last = m.index + m[0].length;
            }
            if (last < text.length) segments.push({ text: text.slice(last), type: "static" });
            return { segments, plain: segments.map(s => s.text).join("") };
          };

          // ── CSV export ──
          const fmlExportCSV = () => {
            const esc = s => `"${(s || "").replace(/"/g, '""')}"`;
            const varKeys = Object.keys(fmlVars);
            const maxLen = Math.max(1, ...varKeys.map(k => (fmlVars[k] || []).length));
            const lines = [["Переменная", "Описание", ...Array.from({ length: maxLen }, (_, i) => `Вариант ${i + 1}`)].map(esc).join(",")];
            varKeys.forEach(k => { lines.push([k, fmlDefs[k]?.label || "", ...(fmlVars[k] || [])].map(esc).join(",")); });
            lines.push(""); lines.push(esc("--- Шаблон текста ---"));
            lines.push(esc(fmlTemplate));
            fmlVariants.forEach((v, i) => { lines.push(""); lines.push(esc(`--- Вариант ${i + 2} ---`)); lines.push(esc(v)); });
            const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
            const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${proj?.name || "formula"}_formula.csv`; a.click();
          };

          if (!fmlLoaded) return <div style={{ ...card, textAlign: "center", padding: 30 }}>Загрузка...</div>;

          return (
            <div>
              {/* Generate button + progress */}
              <div style={card}>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <button style={{ ...btnP, opacity: fmlBusy ? .6 : 1 }} onClick={fmlAutoGenerate} disabled={fmlBusy}>{fmlBusy ? "Формируется..." : "Сформировать формулу"}</button>
                  <button style={btnG} onClick={fmlAddVar} disabled={fmlBusy}>+ Переменная</button>
                </div>
                {fmlBusy && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 12, color: "#6366f1", fontWeight: 600 }}>{fmlStep.text}</span>
                      <span style={{ fontSize: 12, color: "#999" }}>{fmlStep.pct}%</span>
                    </div>
                    <div style={{ background: "#e5e7eb", borderRadius: 8, height: 8, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${fmlStep.pct}%`, background: "linear-gradient(90deg,#6366f1,#a855f7)", borderRadius: 8, transition: "width .4s ease" }} />
                    </div>
                  </div>
                )}
              </div>

              {/* Variables panel */}
              {Object.keys(fmlDefs).length > 0 && (
                <div style={card}>
                  <p style={stitle}>Переменные ({Object.keys(fmlDefs).length})</p>
                  {Object.keys(fmlDefs).map(vn => (
                    <div key={vn} style={{ marginBottom: 14, borderLeft: "3px solid #a78bfa", paddingLeft: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                        <span style={{ background: "#ede9fe", color: "#6d28d9", padding: "2px 8px", borderRadius: 5, fontSize: 12, fontWeight: 700 }}>{`{${vn}}`}</span>
                        <input value={fmlDefs[vn]?.label || ""} onChange={e => fmlUpdateLabel(vn, e.target.value)} placeholder="Описание..." style={{ ...inp, width: 180, fontSize: 11, padding: "3px 6px" }} />
                        <button style={{ ...btnSm, marginLeft: "auto" }} onClick={() => fmlAiGenerate(vn)} disabled={fmlGenTag === vn}>{fmlGenTag === vn ? "..." : "AI"}</button>
                        <button style={{ ...btnSm, color: "#ef4444" }} onClick={() => fmlRemoveVar(vn)}>x</button>
                      </div>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {(fmlVars[vn] || []).map((v, i) => (
                          <div key={i} style={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <input value={v} onChange={e => fmlUpdateVariant(vn, i, e.target.value)} style={{ ...inp, width: 150, fontSize: 11, padding: "3px 6px" }} />
                            <button style={{ border: "none", background: "none", color: "#ef4444", cursor: "pointer", fontSize: 10, padding: 0 }} onClick={() => fmlRemoveVariant(vn, i)}>x</button>
                          </div>
                        ))}
                        <button style={{ ...btnSm, fontSize: 10 }} onClick={() => fmlAddVariant(vn)}>+</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Actions */}
              <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
                <button style={btnP} onClick={fmlSave} disabled={fmlSaving}>{fmlSaving ? "..." : "Сохранить"}</button>
                <button style={{ ...btnG, background: "#dbeafe", color: "#1e40af", fontWeight: 600, opacity: fmlTemplate ? 1 : .4 }} onClick={() => fmlTemplate && setFmlExample(buildExample())} disabled={!fmlTemplate}>Пример текста</button>
                <button style={{ ...btnG, background: "#d1fae5", color: "#065f46", fontWeight: 600, opacity: fmlTemplate ? 1 : .4 }} onClick={() => fmlTemplate && fmlExportCSV()} disabled={!fmlTemplate}>Скачать CSV</button>
              </div>

              {/* Example modal */}
              {fmlExample !== null && (
                <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }} onClick={() => setFmlExample(null)}>
                  <div style={{ background: t.bgCard, borderRadius: 16, padding: 28, maxWidth: 750, width: "100%", maxHeight: "85vh", overflowY: "auto", border: `1px solid ${t.border}` }} onClick={e => e.stopPropagation()}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
                      <h3 style={{ fontWeight: 700, fontSize: 16 }}>Пример готового текста</h3>
                      <button style={btnSm} onClick={() => setFmlExample(null)}>x</button>
                    </div>
                    <div style={{ background: dark ? "#252535" : "#f9fafb", borderRadius: 10, padding: 20, lineHeight: 1.9, fontSize: 15, whiteSpace: "pre-wrap" }}>
                      {fmlExample && fmlExample.segments ? fmlExample.segments.map((seg, i) =>
                        seg.type === "anchor"
                          ? <span key={i} style={{ background: "#fce7f3", color: "#be185d", borderRadius: 4, padding: "2px 5px", fontWeight: 700 }} title={`Анкор: {${seg.varName}}`}>{seg.text}</span>
                          : seg.type === "text"
                          ? <span key={i} style={{ background: "#dbeafe", color: "#1e40af", borderRadius: 4, padding: "2px 5px" }} title={`Текст: {${seg.varName}}`}>{seg.text}</span>
                          : <span key={i}>{seg.text}</span>
                      ) : fmlExample}
                    </div>
                    <div style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button style={btnP} onClick={() => setFmlExample(buildExample())}>Другой вариант (основной)</button>
                      {fmlVariants.map((vt, i) => (
                        <button key={i} style={btnG} onClick={() => setFmlExample(buildExample(vt))}>Вариант {i + 2}</button>
                      ))}
                      <button style={btnG} onClick={() => { navigator.clipboard.writeText(fmlExample?.plain || ""); flash("Скопировано"); }}>Копировать</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Import for prod/filter */}
        {pageType !== "formula" && pageType !== "catalog" && cur.length === 0 && (
          <div style={{ ...card, textAlign: "center", padding: 24 }}>
            <p style={{ fontWeight: 600, marginBottom: 12 }}>{pageType === "product" ? "🛍 Загрузите товары" : "🔍 Загрузите фильтры"}</p>
            <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
              <button style={btnP} onClick={() => fRef.current?.click()}>📄 CSV</button>
              <button style={btnG} onClick={() => setShowImp(true)}>📋 Вставить</button>
              <input ref={fRef} type="file" accept=".csv,.tsv,.txt" hidden onChange={e => e.target.files[0] && handleCSV(e.target.files[0])} />
            </div>
            <p style={{ ...muted, marginTop: 8 }}>CSV: url, name</p>
          </div>
        )}
        {pageType !== "formula" && pageType !== "catalog" && cur.length > 0 && (
          <div style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "center" }}>
            <span style={{ fontSize: 12, color: "#16a34a" }}>✓ {cur.length} стр.</span>
            <button style={btnSm} onClick={() => fRef.current?.click()}>+ CSV</button>
            <button style={btnSm} onClick={() => setShowImp(true)}>+ Текст</button>
            <input ref={fRef} type="file" accept=".csv,.tsv,.txt" hidden onChange={e => e.target.files[0] && handleCSV(e.target.files[0])} />
          </div>
        )}

        {/* Import modal */}
        {showImp && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={() => setShowImp(false)}>
            <div style={{ background: t.bgCard, borderRadius: 16, padding: 24, width: 500, maxWidth: "90vw", border: `1px solid ${t.border}` }} onClick={e => e.stopPropagation()}>
              <h3 style={{ fontWeight: 700, marginBottom: 10 }}>Вставьте URL</h3>
              <p style={{ ...muted, marginBottom: 8 }}>По одному на строку: URL, название</p>
              <textarea value={impTxt} onChange={e => setImpTxt(e.target.value)} rows={8} placeholder="https://site.com/product1, Стакан\nhttps://site.com/product2, Тарелка" style={{ ...inp, resize: "vertical", fontSize: 12, fontFamily: "monospace" }} />
              <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}>
                <button style={btnG} onClick={() => setShowImp(false)}>Отмена</button>
                <button style={btnP} onClick={handleTxtImport}>✓ Импорт</button>
              </div>
            </div>
          </div>
        )}

        {pageType !== "formula" && cur.length > 0 && <>
          {/* Stats */}
          <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
            {[["Всего", cn.all, dark ? t.text : "#333"], ["✓", cn.done, "#16a34a"], ["Ожидает", cn.ready, "#2563eb"], ["✕", cn.error, "#dc2626"]].map(([l, n, c], i) => (
              <div key={i} style={{ background: dark ? "#252535" : "#f9fafb", borderRadius: 10, padding: "8px 14px", textAlign: "center", minWidth: 75 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: c }}>{n}</div>
                <div style={{ fontSize: 10, color: t.textMuted, textTransform: "uppercase" }}>{l}</div>
              </div>
            ))}
          </div>

          {/* Controls */}
          <div style={{ ...card, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <button style={{ ...btnP, opacity: run ? .6 : 1 }} onClick={generate} disabled={run || cn.ready === 0}>
              {run ? `⏳ ${prog.d}/${prog.t} (${pct}%)` : "▶ Генерировать"}
            </button>
            {run && <button style={{ ...btnG, color: "#dc2626" }} onClick={() => abRef.current = true}>⏹</button>}
            <span style={muted}>Batch:</span>
            <select value={bs} onChange={e => setBs(+e.target.value)} disabled={run} style={{ padding: "4px 6px", fontSize: 12, borderRadius: 6, border: `1px solid ${t.inputBorder}`, background: t.inputBg, color: t.text }}>
              {[1, 2].map(v => <option key={v} value={v}>{v}</option>)}
            </select>
            <span style={muted}>Лимит:</span>
            <select value={lim} onChange={e => setLim(+e.target.value)} disabled={run} style={{ padding: "4px 6px", fontSize: 12, borderRadius: 6, border: `1px solid ${t.inputBorder}`, background: t.inputBg, color: t.text }}>
              <option value={0}>Все ({cn.ready})</option>
              {[5, 10, 25, 50, 100].map(v => <option key={v} value={v}>{v}</option>)}
            </select>
            <span style={muted}>Посилання:</span>
            <select value={linkRange} onChange={e => setLinkRange(e.target.value)} disabled={run} title="Скільки внутрішніх посилань вставляти в текст (щоб не переспамлювати)" style={{ padding: "4px 6px", fontSize: 12, borderRadius: 6, border: `1px solid ${t.inputBorder}`, background: t.inputBg, color: t.text }}>
              {["3-5", "6-10", "10-15", "15-25"].map(v => <option key={v} value={v}>{v}</option>)}
            </select>
            {cn.done > 0 && <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
              <button style={btnSm} onClick={expHTML}>⬇ HTML</button>
              <button style={btnSm} onClick={expCSV}>⬇ CSV</button>
              <button style={btnSm} onClick={expSheet}>⬇ Google Sheets</button>
            </div>}
          </div>

          {/* Progress */}
          {(run || prog.t > 0) && <div style={{ background: dark ? "#252535" : "#e5e7eb", borderRadius: 8, height: 24, marginBottom: 14, overflow: "hidden", position: "relative" }}>
            <div style={{ height: "100%", width: `${pct}%`, background: "linear-gradient(90deg,#6366f1,#a855f7)", borderRadius: 8, transition: "width .3s" }} />
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600 }}>{prog.d}/{prog.t} — {pct}%</div>
          </div>}

          {/* Log */}
          {logs.length > 0 && <>
            <button style={{ ...btnSm, marginBottom: 8 }} onClick={() => setShowLog(!showLog)}>{showLog ? "▼" : "▶"} Лог ({logs.length})</button>
            {showLog && <div ref={lgRef} style={{ maxHeight: 140, overflowY: "auto", background: "#1e1b2e", color: "#a6e3a1", borderRadius: 8, padding: 10, fontFamily: "monospace", fontSize: 11, marginBottom: 14, lineHeight: 1.5 }}>
              {logs.map((l, i) => <div key={i} style={{ color: l.t === "err" ? "#f87171" : l.t === "warn" ? "#fbbf24" : "#86efac" }}><span style={{ opacity: .4 }}>{l.ts}</span> {l.m}</div>)}
            </div>}
          </>}

          {/* Filter buttons */}
          <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
            {[["all", "Все", cn.all], ["done", "✓", cn.done], ["ready", "Ожидает", cn.ready], ["error", "✕", cn.error]].map(([k, l, n]) => (
              <button key={k} style={{ ...(flt === k ? btnP : btnSm), fontSize: 11 }} onClick={() => { setFlt(k); setPg(0); }}>{l} ({n})</button>
            ))}
          </div>

          {/* Preview modal */}
          {pvItem?.content && (
            <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }} onClick={() => setPvId(null)}>
              <div style={{ background: t.bgCard, borderRadius: 16, padding: 28, maxWidth: 750, width: "100%", maxHeight: "85vh", overflowY: "auto", border: `1px solid ${t.border}` }} onClick={e => e.stopPropagation()}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                  <span style={{ fontSize: 11, color: t.textMuted }}>{pvItem.url}</span>
                  <button style={btnSm} onClick={() => setPvId(null)}>✕</button>
                </div>
                <h2 style={{ fontSize: 18, fontWeight: 700, borderBottom: "2px solid #6366f1", paddingBottom: 8, marginBottom: 16 }}>{pvItem.content.h2}</h2>
                <div style={{ lineHeight: 1.7, fontSize: 14 }} dangerouslySetInnerHTML={{ __html: hlHTML(pvItem.content.html) }} />
                <div style={{ marginTop: 16, padding: 12, background: dark ? "#252535" : "#f9fafb", borderRadius: 8, fontSize: 12 }}>
                  <div style={{ display: "flex", gap: 16 }}>
                    <span>📊 <b>{pvItem.content.html?.replace(/<[^>]+>/g, "").length || 0}</b> символов</span>
                    <span>🔗 <b>{(pvItem.content.html?.match(/<a /g) || []).length}</b> анкоров</span>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <span style={{ display: "inline-block", background: "#dbeafe", border: "1px solid #93c5fd", borderRadius: 4, padding: "1px 6px", fontSize: 11, marginRight: 8 }}>🔗 синий = анкор</span>
                    <span style={{ display: "inline-block", background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 4, padding: "1px 6px", fontSize: 11 }}>обычный = статика</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Table */}
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead><tr>
                <th style={{ ...th, width: 155 }}>URL / Ключ</th><th style={th}>H2</th><th style={th}>Превью</th>
                <th style={{ ...th, width: 50 }}>Симв</th><th style={{ ...th, width: 45 }}>🔗</th>
                <th style={{ ...th, width: 60 }}>Ст.</th><th style={{ ...th, width: 50 }}></th>
              </tr></thead>
              <tbody>{pgD.map(c => {
                const ac = c.content?.html ? (c.content.html.match(/<a /g) || []).length : 0;
                const ch = c.content?.html ? c.content.html.replace(/<[^>]+>/g, "").length : 0;
                return (
                  <tr key={c.id} style={{ background: c.cSt === "GENERATING" ? (dark ? "rgba(99,102,241,.1)" : "#eff6ff") : "" }}>
                    <td style={td}><span style={{ fontSize: 10, color: "#999", wordBreak: "break-all" }}>{c.url.replace(/https?:\/\/[^/]+/, "")}</span><br /><span style={{ fontSize: 11, color: "#6366f1", fontWeight: 600 }}>{c.hfKeyword}</span></td>
                    <td style={td}>{c.content?.h2 ? <span style={{ fontSize: 12, fontWeight: 600 }}>{c.content.h2}</span> : <span style={muted}>—</span>}</td>
                    <td style={td}>{c.content?.html ? <span style={{ fontSize: 11, color: t.textMuted, cursor: "pointer" }} onClick={() => setPvId(c.id)}>{c.content.html.replace(/<[^>]+>/g, "").slice(0, 65)}... <span style={{ color: "#6366f1" }}>👁</span></span> : <span style={muted}>—</span>}</td>
                    <td style={{ ...td, textAlign: "center", fontSize: 11 }}>{ch || "—"}</td>
                    <td style={{ ...td, textAlign: "center", fontSize: 11, fontWeight: 700, color: ac >= 25 ? "#16a34a" : ac > 0 ? "#ca8a04" : "#999" }}>{ac || "—"}</td>
                    <td style={td}><Badge st={c.content ? "DONE" : c.cSt === "ERROR" ? "ERROR" : c.cSt === "GENERATING" ? "GENERATING" : c.status} /></td>
                    <td style={td}>
                      {c.content && <button style={btnSm} onClick={() => setPvId(c.id)}>👁</button>}
                      <button style={btnSm} onClick={() => regenOne(c)} disabled={run}>↻</button>
                    </td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
          {tPg > 1 && <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 12 }}>
            <button style={btnSm} disabled={pg === 0} onClick={() => setPg(pg - 1)}>←</button>
            <span style={{ fontSize: 12, lineHeight: "28px" }}>{pg + 1}/{tPg}</span>
            <button style={btnSm} disabled={pg >= tPg - 1} onClick={() => setPg(pg + 1)}>→</button>
          </div>}
        </>}
      </div>
    );
}
